import React from 'react';
import {theme} from 'antd';
import './ArchiX6.css';
import DiagramNavigatorTree from '@components/diagram/DiagramNavigatorTree';
import DiagramNameModal from '@components/archiX6/DiagramNameModal';
import ArchiX6ConnectionTypePicker from '@components/archiX6/ArchiX6ConnectionTypePicker';
import PropertiesModal from '@components/dialog/PropertiesModal';
import ArchiX6ContextMenu from '@components/archiX6/ArchiX6ContextMenu';
import ArchiX6Minimap from '@components/archiX6/ArchiX6Minimap';
import ArchiX6SidebarPalette from '@components/archiX6/ArchiX6SidebarPalette';
import ArchiX6ViewPickerModal from '@components/archiX6/ArchiX6ViewPickerModal';
import ArchiX6ViewpointModal from '@components/archiX6/ArchiX6ViewpointModal';
import {useUndoRedoShortcuts} from '@/hooks/diagram/useUndoRedoShortcuts';
import {useArchiX6Modeler} from '@/hooks/diagram/useArchiX6Modeler';
import {useArchiX6EditorEffects} from '@/hooks/diagram/useArchiX6EditorEffects';
import {useArchiX6DiagramHandlers} from '@/hooks/diagram/useArchiX6DiagramHandlers';
import {useRemoteCursorTracking} from '@/hooks/diagram/useRemoteCursorTracking';
import RemoteCursorsOverlay from '@components/diagram/RemoteCursorsOverlay';
import ObjectLocksOverlay from '@components/diagram/ObjectLocksOverlay';
import {getViewLabel} from '@/utils/diagram/getViewLabel';

/** Модули, при правке которых в dev пересоздаём редактор целиком (как после перезагрузки страницы). */
const ARCHI_X6_HMR_DEPS = [
	'./ArchiX6.jsx',
	'../../hooks/diagram/useArchiX6Modeler.js',
	'../../hooks/diagram/useArchiX6EditorEffects.js',
	'../../hooks/diagram/useArchiX6DiagramHandlers.js',
	'../../hooks/diagram/useUndoRedoShortcuts.js',
	'../../hooks/diagram/useDiagramWs.ts',
	'../../hooks/diagram/useDiagramEditorData.ts',
	'../../hooks/diagram/useProjectCatalog.ts',
	'../../hooks/diagram/useRemoteCursorTracking.ts',
	'../../core_x6/lib/plugins/objectLockPlugin.js',
	'../../core_x6/lib/plugins/nodeChangePatchPlugin.js',
	'../../core_x6/lib/plugins/edgeVerticesPlugin.js',
	'../../core_x6/lib/ModelerX6.js',
	'../../core_x6/lib/modelerX6/viewElements.js',
	'../../core_x6/lib/modelerX6/ConnectionModel.js',
	'../../core_x6/lib/modelerX6/renderView.js',
];

/** Dev: колбэк из import.meta.hot.accept (должен быть на верхнем уровне модуля). */
let __archiX6DevHmrBump = () => {};

if (import.meta.env.DEV && import.meta.hot) {
	import.meta.hot.accept(ARCHI_X6_HMR_DEPS, () => {
		__archiX6DevHmrBump();
	});
}

const ArchiX6Inner = React.forwardRef(
	(
		{
			initialXml,
			initialJson,
			initialDiagramName,
			initialViewId,
			onViewChange,
			onSave,
			onApplyPatch,
			onInfoClick,
			onClose,
			assistantSlot,
			diagramCollaboration,
			catalog,
			readOnly = false,
			remoteCursors,
			objectLocks,
			clientSessionId,
			onCursorSend,
			onLiveSend,
			onLockSend,
		} = {},
		ref
	) => {
		const {
			diagramContainerRef,
			minimapContainerRef,
			modelerRef,
			modelerReady,
			viewReady,
			views,
			activeViewId,
			setActiveViewId,
			modelContent,
			syncViews,
			applyRemotePatch,
			applyVersionNavigate,
			applyCatalogChange,
			applyLiveGeometry,
			applyLockEvent,
		} = useArchiX6Modeler({
			initialXml,
			initialJson,
			initialDiagramName,
			initialViewId,
			onApplyPatch,
			onLive: onLiveSend,
			onLock: onLockSend,
			clientSessionId,
			readOnly,
		});

		const [navigateViewPickerOpen, setNavigateViewPickerOpen] = React.useState(false);
		const [navigateViewPickerList, setNavigateViewPickerList] = React.useState([]);
		const [viewModalMode, setViewModalMode] = React.useState(null);
		const [viewModalName, setViewModalName] = React.useState('');
		const [viewModalEditingId, setViewModalEditingId] = React.useState(null);
		const [paletteCollapsed, setPaletteCollapsed] = React.useState(false);
		const [selectionMode, setSelectionMode] = React.useState(false);
		const [contextMenu, setContextMenu] = React.useState(null);
		const [connectionTypePicker, setConnectionTypePicker] = React.useState(null);
		const [viewpointModalOpen, setViewpointModalOpen] = React.useState(false);
		const [propertiesDrawerOpen, setPropertiesDrawerOpen] = React.useState(false);
		const [propertiesDrawerElement, setPropertiesDrawerElement] = React.useState(null);
		const navigateViewPickerConceptRef = React.useRef({conceptId: null, isRelationship: false});
		const {
			token: {colorBorder},
		} = theme.useToken();

		useUndoRedoShortcuts(modelerRef, modelerReady && viewReady && !readOnly, diagramCollaboration ?? null);

		useRemoteCursorTracking({
			enabled: typeof onCursorSend === 'function',
			containerRef: diagramContainerRef,
			modelerRef,
			modelerReady,
			viewReady,
			activeViewId,
			onCursorSend,
		});

		useArchiX6EditorEffects({
			modelerRef,
			modelerReady,
			viewReady,
			contextMenu,
			setContextMenu,
			connectionTypePicker,
			setConnectionTypePicker,
			setViewpointModalOpen,
			propertiesDrawerOpen,
			setPropertiesDrawerElement,
			readOnly,
		});

		const {
			save,
			applyViewpointFromModal,
			onViewSelect,
			deleteConceptFromView,
			addConceptToView,
			isConceptOnCurrentView,
			isConceptOnAnyView,
			deleteConceptFromModel,
			onNavigateToConcept,
			onNavigateViewPickerSelect,
			onDeleteView,
			onAddView,
			onEditView,
			onViewModalOk,
			onSettingsClick,
			openPropertiesFromContextMenu,
			deleteNodeFromModelByCellId,
		} = useArchiX6DiagramHandlers({
			modelerRef,
			syncViews,
			views,
			activeViewId,
			setActiveViewId,
			onViewChange,
			onApplyPatch,
			onSave,
			navigateViewPickerConceptRef,
			setNavigateViewPickerOpen,
			setNavigateViewPickerList,
			setViewModalMode,
			setViewModalName,
			setViewModalEditingId,
			setPropertiesDrawerOpen,
			setPropertiesDrawerElement,
			setViewpointModalOpen,
			viewModalMode,
			viewModalName,
			viewModalEditingId,
			setSelectionMode,
			catalog,
		});

		React.useImperativeHandle(
			ref,
			() => ({
				save,
				applyRemotePatch,
				applyVersionNavigate,
				applyCatalogChange,
				applyLiveGeometry,
				applyLockEvent,
				focusCanvasElementById: id => modelerRef.current?.focusCanvasElementById?.(id),
				getModeler: () => modelerRef.current,
			}),
			[save, applyRemotePatch, applyVersionNavigate, applyCatalogChange, applyLiveGeometry, applyLockEvent]
		);

		return (
			<>
				<div
					style={{
						display: 'flex',
						width: '100%',
						height: '100%',
						minWidth: 0,
						border: `1px solid ${colorBorder}`,
						borderRadius: 'inherit',
					}}
				>
					<div
						style={{
							flex: 1,
							position: 'relative',
							minWidth: 0,
							borderRadius: 'inherit',
						}}
					>
						<div
							ref={diagramContainerRef}
							style={{width: '100%', height: '100%', borderRadius: 'inherit'}}
						/>
						<RemoteCursorsOverlay
							cursors={remoteCursors ?? []}
							activeViewId={activeViewId}
							containerRef={diagramContainerRef}
							modelerRef={modelerRef}
							modelerReady={modelerReady}
							viewReady={viewReady}
						/>
						<ObjectLocksOverlay
							locks={objectLocks ?? []}
							localSessionId={clientSessionId ?? null}
							activeViewId={activeViewId}
							containerRef={diagramContainerRef}
							modelerRef={modelerRef}
							modelerReady={modelerReady}
							viewReady={viewReady}
						/>
						<ArchiX6Minimap ref={minimapContainerRef} />
					</div>
					{!readOnly && (
					<ArchiX6SidebarPalette
						colorBorder={colorBorder}
						paletteCollapsed={paletteCollapsed}
						setPaletteCollapsed={setPaletteCollapsed}
						modelerReady={modelerReady}
						modeler={modelerRef.current}
						selectionMode={selectionMode}
						onToggleSelectionMode={() => {
							const modeler = modelerRef.current;
							if (!modeler?.enablePaletteBoxSelectMode) return;
							setSelectionMode(prev => {
								const next = !prev;
								if (next) modeler.enablePaletteBoxSelectMode();
								else modeler.disablePaletteBoxSelectMode();
								return next;
							});
						}}
					/>
					)}
				</div>
				<DiagramNavigatorTree
					diagramName={initialDiagramName ?? ''}
					views={views}
					activeViewId={activeViewId}
					modelContent={modelContent}
					onViewChange={onViewSelect}
					onAddView={readOnly ? undefined : onAddView}
					onEditView={readOnly ? undefined : onEditView}
					onDeleteView={readOnly ? undefined : onDeleteView}
					onSettingsClick={readOnly ? undefined : onSettingsClick}
					onInfoClick={onInfoClick}
					assistantSlot={assistantSlot}
					onClose={onClose}
					onNavigateToConcept={onNavigateToConcept}
					onAddConceptToView={readOnly ? undefined : addConceptToView}
					onDeleteConceptFromView={readOnly ? undefined : deleteConceptFromView}
					onDeleteConceptFromModel={readOnly ? undefined : deleteConceptFromModel}
					isConceptOnCurrentView={isConceptOnCurrentView}
					isConceptOnAnyView={isConceptOnAnyView}
					canDelete={views.length > 1 && !!activeViewId}
					catalog={catalog}
				/>
				<ArchiX6ViewPickerModal
					open={navigateViewPickerOpen}
					items={navigateViewPickerList}
					onCancel={() => {
						setNavigateViewPickerOpen(false);
						setNavigateViewPickerList([]);
					}}
					onSelect={viewId => {
						const {conceptId, isRelationship} = navigateViewPickerConceptRef.current;
						onNavigateViewPickerSelect(viewId, conceptId, isRelationship);
					}}
				/>
				<PropertiesModal
					modeler={modelerRef.current}
					isOpen={propertiesDrawerOpen}
					element={propertiesDrawerElement}
					onClose={() => {
						setPropertiesDrawerOpen(false);
						setPropertiesDrawerElement(null);
					}}
				/>
				<DiagramNameModal
					mode={viewModalMode}
					open={!!viewModalMode}
					name={viewModalName}
					onNameChange={setViewModalName}
					onOk={onViewModalOk}
					onCancel={() => setViewModalMode(null)}
				/>
				{!readOnly && (
				<ArchiX6ContextMenu
					contextMenu={contextMenu}
					modeler={modelerRef.current}
					diagramCollaboration={diagramCollaboration ?? null}
					onClose={() => setContextMenu(null)}
					onOpenViewpoint={() => setViewpointModalOpen(true)}
					onOpenProperties={openPropertiesFromContextMenu}
					onDeleteNodeFromModel={deleteNodeFromModelByCellId}
				/>
				)}
				<ArchiX6ViewpointModal
					open={viewpointModalOpen}
					initialValue={modelerRef.current?.getActiveViewViewpoint?.() ?? null}
					activeViewLabel={
						getViewLabel(views.find(v => (v.id ?? v.$attrs?.id) === activeViewId)) ||
						'—'
					}
					onApply={applyViewpointFromModal}
					onCancel={() => setViewpointModalOpen(false)}
				/>
				<ArchiX6ConnectionTypePicker
					picker={connectionTypePicker}
					modeler={modelerRef.current}
					onClose={() => setConnectionTypePicker(null)}
				/>
			</>
		);
	}
);

ArchiX6Inner.displayName = 'ArchiX6Inner';

const ArchiX6 = React.forwardRef((props, ref) => {
	const [devHmrRemountKey, setDevHmrRemountKey] = React.useState(0);

	React.useEffect(() => {
		if (!import.meta.env.DEV) return;
		__archiX6DevHmrBump = () => setDevHmrRemountKey(k => k + 1);
		return () => {
			__archiX6DevHmrBump = () => {};
		};
	}, []);

	return <ArchiX6Inner key={devHmrRemountKey} {...props} ref={ref} />;
});

ArchiX6.displayName = 'ArchiX6';

export default ArchiX6;
